import type { UseFormReset } from "react-hook-form";
import type { FieldValues } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import type { SiteTheme } from "@circle-react-shared/BuilderUI/SiteThemes/constants";
import type { Appearance } from "@circle-react-shared/BuilderUI/Store/interfaceSlice";
import { Button } from "@circle-react-shared/uikit/Button";
import { Typography } from "@circle-react-shared/uikit/Typography";
import { Form } from "@circle-react-uikit/Form";
import { Modal } from "@circle-react-uikit/ModalV2";
import { useCreateCustomSiteTheme } from "../../useCreateCustomSiteTheme";
import { CREATE_CUSTOM_THEME_FORM_VALIDATION_SCHEMA } from "./constants";

interface CreateCustomThemeWithNameModalProps {
  appearance: Appearance;
  currentCanvasTheme: SiteTheme;
  reset: UseFormReset<FieldValues>;
  toggleCurrentCanvasTheme: (theme: SiteTheme) => void;
}

interface FormData {
  theme_name: string;
}

const CreateCustomThemeWithNameModalComponent = ({
  currentCanvasTheme,
  toggleCurrentCanvasTheme,
  appearance,
  reset,
}: CreateCustomThemeWithNameModalProps) => {
  const modal = useModal();

  const { mutate: createCustomSiteTheme, isLoading: isCreatingTheme } =
    useCreateCustomSiteTheme({
      toggleCurrentCanvasTheme,
      reset,
    });

  const handleSubmit = (formData: FormData) => {
    createCustomSiteTheme(
      {
        name: formData.theme_name,
        appearance,
        cssVariables: currentCanvasTheme.cssVariables,
        setAsDefaultTheme: false,
      },
      {
        onSuccess: () => {
          void modal.hide();
        },
      },
    );
  };

  return (
    <Modal
      isOpen={modal.visible}
      onClose={() => {
        if (isCreatingTheme) return;

        void modal.hide();
      }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Form
          validationSchema={CREATE_CUSTOM_THEME_FORM_VALIDATION_SCHEMA}
          defaultValues={{ theme_name: "" }}
          onSubmit={handleSubmit}
        >
          <Modal.Header>
            <Modal.Title size="sm" className="font-bold">
              {t(
                "builder_ui.theme_configuration.create_custom_theme_modal.title",
              )}
            </Modal.Title>
            <Modal.HeaderActions>
              <Modal.CloseButton
                onClick={modal.hide}
                disabled={isCreatingTheme}
              />
            </Modal.HeaderActions>
          </Modal.Header>
          <Modal.Body className="overflow-y-auto pb-11 md:!px-8">
            <Typography.BodyMd color="text-dark">
              {t(
                "builder_ui.theme_configuration.create_custom_theme_modal.description",
              )}
            </Typography.BodyMd>
            <Form.Item
              name="theme_name"
              label={t(
                "builder_ui.theme_configuration.create_custom_theme_modal.form.name.label",
              )}
              hideDescription
              hideBorder
              placeholder={t(
                "builder_ui.theme_configuration.create_custom_theme_modal.form.name.placeholder",
              )}
            >
              <Form.Input name="theme_name" />
            </Form.Item>
          </Modal.Body>
          <Modal.Footer className="border-primary flex w-full justify-end gap-4 border-t">
            <Button
              variant="secondary"
              onClick={modal.hide}
              type="button"
              disabled={isCreatingTheme}
            >
              {t(
                "builder_ui.theme_configuration.create_custom_theme_modal.secondary_cta",
              )}
            </Button>
            <Button variant="circle" type="submit" disabled={isCreatingTheme}>
              {t(
                "builder_ui.theme_configuration.create_custom_theme_modal.primary_cta",
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal.Content>
    </Modal>
  );
};

const CreateCustomThemeWithNameModal = ModalManager.create(
  ({
    currentCanvasTheme,
    toggleCurrentCanvasTheme,
    appearance,
    reset,
  }: CreateCustomThemeWithNameModalProps) => (
    <CreateCustomThemeWithNameModalComponent
      currentCanvasTheme={currentCanvasTheme}
      toggleCurrentCanvasTheme={toggleCurrentCanvasTheme}
      reset={reset}
      appearance={appearance}
    />
  ),
);

export const useCreateCustomThemeWithNameModal = () =>
  useModal(CreateCustomThemeWithNameModal);
